import { Component, computed, inject, input, signal, TemplateRef, ViewChild } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Catalog, StrategyEx, StrategyGroupEx } from "@front/m19-models";
import { AsinSelectionComponent } from "@m19-board/shared/asin-selection/asin-selection.component";
import {
  ConfirmPopupComponent,
  IButtonComponent,
  MODAL_DATA,
  ModalRef,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import { AccountMarketplace, Strategy, StrategyType } from "@front/m19-api-client";
import { AccountSelectionService, AsinService, StrategyService } from "@front/m19-services";
import { forkJoin, map, switchMap, tap } from "rxjs";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  StrategyGroupMoveProductModalComponent,
  StrategyGroupMoveProductModalData,
  TargetProductStrategy,
} from "../../move-product/strategy-group-move-product-modal.component";

export interface StrategyGroupProductsModalData {
  strategyGroup: StrategyGroupEx;
  strategy: StrategyEx;
  isReadOnly: boolean;
}

@Component({
  selector: "strategy-products-modal",
  standalone: true,
  imports: [CommonModule, AsinSelectionComponent, IButtonComponent, TranslocoDirective],
  templateUrl: "./strategy-products-modal.component.html",
})
export class StrategyProductsModalComponent {
  private readonly asinService = inject(AsinService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly strategyService = inject(StrategyService);
  private readonly toastService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly modalService = inject(ModalService);

  @ViewChild("addProductModal") addProductModal!: TemplateRef<any>;
  addProductModalRef: ModalRef<any> | undefined;

  modalData = inject(MODAL_DATA) as StrategyGroupProductsModalData;
  modalRef = inject(ModalRef);

  strategyAsins: string[] = [];
  availableAsinsToAdd: string[] | undefined;
  strategyType: StrategyType | undefined;
  strategyPerAsin: Map<string, StrategyEx[]> | undefined;
  catalogAsins: string[] | undefined;

  asinSelection = signal<string[]>([]);
  addAsinSelection = signal<string[]>([]);

  saveAddAsinLabel = computed(() =>
    this.translocoService.translate("sp-substrategy.add_n_products", { count: this.addAsinSelection().length }),
  );

  deleteAsinLoading = signal(false);
  addAsinLoading = signal(false);

  constructor() {
    this.strategyAsins = this.modalData.strategy.asins.map((a) => a.asin);
    this.strategyType = this.modalData.strategy.strategyType;
    this.strategyPerAsin = this.strategyService.getStrategyPerAsin(this.modalData.strategyGroup);
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        switchMap((am: AccountMarketplace) => this.asinService.getCatalog(am.accountId, am.marketplace)),
        map((catalog) =>
          Array.from(catalog.asinOffers.values())
            .map((a) => a.asin)
            .filter((a): a is string => a !== null),
        ),
      )
      .subscribe((catalogAsins: string[]) => {
        this.catalogAsins = catalogAsins;
        this.updateAvailableAsins();
      });
  }

  deleteProductFromStrategy(asins: string[]) {
    if (this.strategyType === StrategyType.PRODUCT) {
      this.deleteProductFromProductStrategy(asins);
    } else {
      this.deleteAsinLoading.set(true);
      this.strategyService.deleteAsinsFromStrategy(this.modalData.strategy, asins).subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate("common.n_deleted_asins", { count: asins.length }));
          this.asinSelection.set([]);
          this.deleteAsinLoading.set(false);
        },
        error: (error) => {
          this.toastService.error(
            this.translocoService.translate("sp-strategy-group-page.product_deletion_error"),
            error,
          );
          this.deleteAsinLoading.set(false);
        },
      });
    }
  }

  private deleteProductFromProductStrategy(asins: string[]) {
    if (!this.strategyPerAsin) return;

    const impactedStrats = this.strategyService.getImpactedStrategiesOnDeleteProduct(asins, this.strategyPerAsin);

    const modalRef = this.modalService.openModal(ConfirmPopupComponent, {
      modalTitle: this.translocoService.translate("sp-strategy-group-page.product_deletion"),
      data: {
        message: this.strategyService.formatConfirmationMessage(asins, impactedStrats),
      },
    });

    modalRef
      .afterClosed()
      .pipe(
        tap(() => this.deleteAsinLoading.set(true)),
        switchMap(() =>
          forkJoin(
            Array.from(impactedStrats.entries()).map(([strat, asins]) =>
              this.strategyService.deleteAsinsFromStrategy(strat, asins),
            ),
          ),
        ),
        // TODO: also stop strategies if no more ASINs
      )
      .subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate("common.n_deleted_asins", { count: asins.length }));
          this.deleteAsinLoading.set(false);
          this.asinSelection.set([]);
          this.modalRef.close();
        },
        error: (error) => {
          this.toastService.error(
            this.translocoService.translate("sp-strategy-group-page.product_deletion_error"),
            error,
          );
          this.deleteAsinLoading.set(false);
        },
      });
  }

  openAddProductModal() {
    this.asinSelection.set([]);
    this.addProductModalRef = this.modalService.openModal(this.addProductModal, {
      modalTitle: this.translocoService.translate("sp-substrategy.add_products"),
    });
  }

  addProductsToStrategy() {
    this.addAsinLoading.set(true);
    this.strategyService.addAsinsToStrategy(this.modalData.strategy, this.addAsinSelection()).subscribe({
      next: (strategy: Strategy) => {
        this.toastService.success(
          this.translocoService.translate("sp-strategy-group-page.products_added_to_strategy"),
          this.translocoService.translate("sp-strategy-group-page.products_added"),
        );
        this.addAsinSelection.set([]);
        this.strategyAsins = strategy.asins?.map((a) => a.asin) ?? [];
        this.updateAvailableAsins();
        this.addProductModalRef?.close();
      },
      error: (e: string) => {
        this.toastService.error(
          e,
          this.translocoService.translate("sp-strategy-group-page.error_adding_products_to_strategy"),
        );
        this.addAsinLoading.set(false);
      },
    });
  }

  private updateAvailableAsins() {
    if (!this.catalogAsins) return;
    if (this.modalData.strategy.strategyType === StrategyType.PRODUCT) {
      this.availableAsinsToAdd = this.catalogAsins.filter((a) => !this.modalData.strategyGroup.asins.includes(a));
    } else {
      this.availableAsinsToAdd = this.modalData.strategyGroup.asins.filter((a) => !this.strategyAsins.includes(a));
    }
  }

  moveProductsMainStrategy(asins: string[]) {
    this.modalService.openModal<StrategyGroupMoveProductModalData, TargetProductStrategy>(
      StrategyGroupMoveProductModalComponent,
      {
        modalTitle: this.translocoService.translate(
          "strategy-group-move-product-modal.move_products_to_another_main_strategy",
        ),
        data: {
          strategyGroup: this.modalData.strategyGroup,
          source: this.modalData.strategy,
          asinsToMove: asins,
        },
      },
    );
  }
}
